import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { RawData_URL, Store } from '../shared/src';
import { AsyncExportService } from './async-export.service';
import type { AsyncExportApiConfig } from './async-export.types';
import { ErrorHandlerService } from './error-handler.service';
import type { ExcelExportProgressSlot } from './excel-export-progress.service';

@Injectable({
  providedIn: 'root',
})
export class RawDataService {
  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService,
    private asyncExportService: AsyncExportService,
  ) { }

  private readonly RAWDATA_API = `${RawData_URL}`;
  private readonly DISPLAY_SHARE_EXPORT_URL = `${RawData_URL}/displayShareExport`;
  private readonly DISPLAY_PLANOGRAM_EXPORT_URL = `${RawData_URL}/displayPlanogramExport`;
  private readonly PROMOTER_SHARE_EXPORT_URL = `${RawData_URL}/promoterShareExport`;
  private readonly PROMOTER_SELLOUT_EXPORT_URL = `${RawData_URL}/promoterSelloutExport`;
  private readonly TRAINING_LIST_EXPORT_URL = `${RawData_URL}/trainingListExport`;
  private readonly TRAINING_ATTENDANCE_EXPORT_URL = `${RawData_URL}/trainingAttendanceExport`;
  private readonly QUIZ_LIST_EXPORT_URL = `${RawData_URL}/quizListExport`;
  private readonly QUIZ_ATTENDEES_EXPORT_URL = `${RawData_URL}/quizAttendeesExport`;
  private readonly QUIZ_RESULT_EXPORT_URL = `${RawData_URL}/quizResultExport`;
  private readonly FIELD_TRAINING_LIST_EXPORT_URL = `${RawData_URL}/fieldTrainingListExport`;
  private readonly FIELD_TRAINING_ATTENDEES_EXPORT_URL = `${RawData_URL}/fieldTrainingAttendeesExport`;
  private readonly GROUP_TRAINING_SUMMARY_EXPORT_URL = `${RawData_URL}/groupTrainingSummaryExport`;
  private readonly GROUP_TRAINING_DETAILS_EXPORT_URL = `${RawData_URL}/groupTrainingDetailsExport`;
  private readonly FIELD_TRAINING_SUMMARY_EXPORT_URL = `${RawData_URL}/fieldTrainingSummaryExport`;
  private readonly FIELD_TRAINING_DETAILS_EXPORT_URL = `${RawData_URL}/fieldTrainingDetailsExport`;
  private readonly QUIZ_ATTENDEES_SUMMARY_EXPORT_URL = `${RawData_URL}/quizAttendeesSummaryExport`;
  private readonly QUIZ_ATTENDEES_DETAILS_EXPORT_URL = `${RawData_URL}/quizAttendeesDetailsExport`;

  /** Shared async-export API config for Display Share (use with AsyncExportService). */
  readonly displayShareExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.DISPLAY_SHARE_EXPORT_URL,
    exportingLabel: 'Exporting display share',
    defaultFileName: 'display-share-export.csv',
  };

  /** Shared async-export API config for Display Planogram (use with AsyncExportService). */
  readonly displayPlanogramExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.DISPLAY_PLANOGRAM_EXPORT_URL,
    exportingLabel: 'Exporting fixture share',
    defaultFileName: 'Fixture-Share-RawData.csv',
  };

  readonly promoterShareExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.PROMOTER_SHARE_EXPORT_URL,
    exportingLabel: 'Exporting promoter share',
    defaultFileName: 'Promoter-Share-RawData.csv',
  };

  /** Shared async-export API config for Promoter Sellout (use with AsyncExportService). */
  readonly promoterSelloutExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.PROMOTER_SELLOUT_EXPORT_URL,
    exportingLabel: 'Exporting promoter sellout',
    defaultFileName: 'Promoter-Sellout-RawData.csv',
  };

  /** Shared async-export API config for Training List (use with AsyncExportService). */
  readonly trainingListExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.TRAINING_LIST_EXPORT_URL,
    exportingLabel: 'Exporting training list',
    defaultFileName: 'Training-List.xlsx',
  };

  /** Shared async-export API config for Training Attendance (use with AsyncExportService). */
  readonly trainingAttendanceExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.TRAINING_ATTENDANCE_EXPORT_URL,
    exportingLabel: 'Exporting training attendance',
    defaultFileName: 'Training-Attendance.xlsx',
  };

  /** Shared async-export API config for Schedule Quiz list. */
  readonly quizListExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.QUIZ_LIST_EXPORT_URL,
    exportingLabel: 'Exporting quiz list',
    defaultFileName: 'Quiz-List.csv',
  };

  /** Shared async-export API config for Schedule Quiz attendees. */
  readonly quizAttendeesExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.QUIZ_ATTENDEES_EXPORT_URL,
    exportingLabel: 'Exporting quiz attendees',
    defaultFileName: 'Quiz-Attendees.csv',
  };

  /** Shared async-export API config for Schedule Quiz result. */
  readonly quizResultExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.QUIZ_RESULT_EXPORT_URL,
    exportingLabel: 'Exporting quiz result',
    defaultFileName: 'Quiz-Result.csv',
  };

  /** Shared async-export API config for Field Training list. */
  readonly fieldTrainingListExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.FIELD_TRAINING_LIST_EXPORT_URL,
    exportingLabel: 'Exporting field training list',
    defaultFileName: 'Field-Training-List.xlsx',
  };

  /** Shared async-export API config for Field Training attendees. */
  readonly fieldTrainingAttendeesExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.FIELD_TRAINING_ATTENDEES_EXPORT_URL,
    exportingLabel: 'Exporting field training attendees',
    defaultFileName: 'Field-Training-Attendees.xlsx',
  };

  /** Shared async-export API config for Group Training summary. */
  readonly groupTrainingSummaryExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.GROUP_TRAINING_SUMMARY_EXPORT_URL,
    exportingLabel: 'Exporting group training summary',
    defaultFileName: 'Group-Training-Summary.xlsx',
  };

  /** Shared async-export API config for Group Training details. */
  readonly groupTrainingDetailsExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.GROUP_TRAINING_DETAILS_EXPORT_URL,
    exportingLabel: 'Exporting group training details',
    defaultFileName: 'Group-Training-Details.csv',
  };

  /** Shared async-export API config for Field Training Details summary. */
  readonly fieldTrainingSummaryExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.FIELD_TRAINING_SUMMARY_EXPORT_URL,
    exportingLabel: 'Exporting field training summary',
    defaultFileName: 'Field-Training-Summary.xlsx',
  };

  /** Shared async-export API config for Field Training Details promoter rows. */
  readonly fieldTrainingDetailsExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.FIELD_TRAINING_DETAILS_EXPORT_URL,
    exportingLabel: 'Exporting field training details',
    defaultFileName: 'Field-Training-Details.csv',
  };

  /** Shared async-export API config for Quiz Attendees Details summary. */
  readonly quizAttendeesSummaryExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.QUIZ_ATTENDEES_SUMMARY_EXPORT_URL,
    exportingLabel: 'Exporting quiz attendees summary',
    defaultFileName: 'Quiz-Attendees-Summary.csv',
  };

  /** Shared async-export API config for Quiz Attendees Details rows. */
  readonly quizAttendeesDetailsExportApi: AsyncExportApiConfig = {
    apiBaseUrl: this.QUIZ_ATTENDEES_DETAILS_EXPORT_URL,
    exportingLabel: 'Exporting quiz attendees details',
    defaultFileName: 'Quiz-Attendees-Details.csv',
  };

  /** POST body for the queued promoter sellout export (filters + dataset flag). */
  buildPromoterSelloutExportStartBody(
    params: Record<string, unknown>,
  ): Record<string, unknown> {
    return { ...params };
  }

  //get all active survey list
  public getAllActiveSurveyList(): Observable<any> {
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/getAllSurvey`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To get all active country list
  public getAllActiveCountry(countryIds?: any): Observable<any> {
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/getAllActiveCountry`, countryIds)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To get all areaGroup list based on the countryids else all areagroup
  public getAreaGroupByCountryIds(countryIds?: number[]): Observable<any> {
    return this.http
      .post<any>(
        `${this.RAWDATA_API}/rawData/getAreaGroupBycountryIds`,
        countryIds
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To get the State value based on the countryId
  public getStateByCountryIdsAndAreaGroup(allIds?: any): Observable<any> {
    return this.http
      .post<any>(
        `${this.RAWDATA_API}/rawData/getStateByCountryIdsAndAreaGroupIds`,
        allIds
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Cities for selected countries; optional stateIds / areaGroupIds narrow the list.
   * Maps to POST `rawData/getCityByIds` (countryIds required for filtering).
   */
  public getCityByIds(params?: {
    countryIds?: number[];
    areaGroupIds?: number[];
    stateIds?: number[];
  }): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/rawData/getCityByIds`, params ?? {})
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To get the Channel
  public getCahnnel(): Observable<any> {
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/getCahnnel`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To get the Account
  public getAccount(allIds?: any): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/rawData/getAccount`, allIds)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To get the store value based on the countryIds, stateIds and cityIds else all store list
  public getStoreByIds(allIds?: any): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/rawData/getStoreByIds`, allIds)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To get the user value based on the countryids, stateids, cityids and storeid else all user list
  public getUserByIds(allIds?: any): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/rawData/getUserByIds`, allIds)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportSurvey(filterValue: any): Observable<Blob> {
    return this.http
      .post<any>(
        `${this.RAWDATA_API}/rawData/downloadExcelReport`,
        { filterValue },
        {
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));

  }

  private exportData(
    endpoint: string,
    columns: string[],
    filterValue: any
  ): Observable<Blob> {
    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/${endpoint}/downloadExcelReport`,
        { columns, filterValue },
        { responseType: 'blob' as 'json' }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSelloutData(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    countryId?: number | string,
    accountId?: number | string,
    channelId?: number | string,
    startDate?: string,
    endDate?: string,
    productId?: number,
    categoryId?: number | string,
    subCategoryId?: number | string,
    storeId?: number | string,
    areaId?: number | string,
    stateId?: number | string,
    userId?: number | string,
    isBundleOffer?: boolean,
    starProduct?: string,
    mdaSda?: string,
    focusStore?: string,
    projectNameId?: string,
    onlyZeroPrice?: boolean,
    mdaSdaName?: string,
    channelType?: string,
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.isBundleOffer !== null && params.isBundleOffer !== undefined) {
      queryParams = queryParams.set('isBundleOffer', params.isBundleOffer.toString());
    }
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }
    if (params.accountId) {
      const accountIds = Array.isArray(params.accountId) ? params.accountId.join(',') : params.accountId;
      queryParams = queryParams.set('accountId', accountIds);
    }
    if (params.channelId) {
      // If array, join as comma-separated string
      const channelIds = Array.isArray(params.channelId) ? params.channelId.join(',') : params.channelId;
      queryParams = queryParams.set('channelId', channelIds);
    }
    if (params.productId) {
      queryParams = queryParams.set('productId', params.productId);
    }
    if (params.categoryId) {
      // If array, join as comma-separated string
      const catIds = Array.isArray(params.categoryId) ? params.categoryId.join(',') : params.categoryId;
      queryParams = queryParams.set('categoryId', catIds);
    }
    if (params.subCategoryId) {
      // If array, join as comma-separated string
      const subCatIds = Array.isArray(params.subCategoryId) ? params.subCategoryId.join(',') : params.subCategoryId;
      queryParams = queryParams.set('subCategoryId', subCatIds);
    }
    if (params.storeId) {
      const storeIds = Array.isArray(params.storeId) ? params.storeId.join(',') : params.storeId;
      queryParams = queryParams.set('storeId', storeIds);
    }
    if (params.startDate) {
      queryParams = queryParams.set('fromDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('toDate', params.endDate);
    }

    if (params.areaId) {
      const areaIds = Array.isArray(params.areaId) ? params.areaId.join(',') : params.areaId;
      queryParams = queryParams.set('areaId', areaIds);
    }
    if (params.stateId) {
      const stateIds = Array.isArray(params.stateId) ? params.stateId.join(',') : params.stateId;
      queryParams = queryParams.set('stateId', stateIds);
    }
    if (params.userId) {
      const userIds = Array.isArray(params.userId) ? params.userId.join(',') : params.userId;
      queryParams = queryParams.set('userId', userIds);
    }
    if (params.starProduct) {
      const starProduct = Array.isArray(params.starProduct)
        ? params.starProduct.join(',')
        : params.starProduct;
      queryParams = queryParams.set('starProduct', starProduct);
    }
    if (params.channelType) {
      const channelType = Array.isArray(params.channelType)
        ? params.channelType.join(',')
        : params.channelType;
      queryParams = queryParams.set('channelType', channelType);
    }
    if (params.mdaSda) {
      queryParams = queryParams.set('mdaSda', params.mdaSda);
    }
    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }
    if (params.projectNameId) {
      queryParams = queryParams.set('projectNameId', params.projectNameId);
    }
    if (params.onlyZeroPrice !== undefined && params.onlyZeroPrice !== null) {
      queryParams = queryParams.set('onlyZeroPrice', params.onlyZeroPrice.toString());
    }
    if (params.mdaSdaName) {
      queryParams = queryParams.set('mdaSdaName', params.mdaSdaName);
    }
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/sellout`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getBundleOfferPriceData(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    countryId?: number | string,
    accountId?: number | string,
    channelId?: number | string,
    startDate?: string,
    endDate?: string,
    productId?: number,
    categoryId?: number | string,
    subCategoryId?: number | string,
    storeId?: number | string,
    areaId?: number | string,
    stateId?: number | string,
    userId?: number | string,
    starProduct?: string,
    mdaSda?: string,
    focusStore?: string,
    projectNameId?: string,
    onlyZeroPrice?: boolean,
    mdaSdaName?: string,
    channelType?: string
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);


    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId.toString());
    }
    if (params.accountId) {
      const accountIds = Array.isArray(params.accountId) ? params.accountId.join(',') : params.accountId;
      queryParams = queryParams.set('accountId', accountIds);
    }
    if (params.channelId) {
      // If array, join as comma-separated string
      const channelIds = Array.isArray(params.channelId) ? params.channelId.join(',') : params.channelId;
      queryParams = queryParams.set('channelId', channelIds);
    }
    if (params.productId) {
      queryParams = queryParams.set('productId', params.productId);
    }
    if (params.categoryId) {
      // If array, join as comma-separated string
      const catIds = Array.isArray(params.categoryId) ? params.categoryId.join(',') : params.categoryId;
      queryParams = queryParams.set('categoryId', catIds);
    }
    if (params.subCategoryId) {
      // If array, join as comma-separated string
      const subCatIds = Array.isArray(params.subCategoryId) ? params.subCategoryId.join(',') : params.subCategoryId;
      queryParams = queryParams.set('subCategoryId', subCatIds);
    }
    if (params.storeId) {
      const storeIds = Array.isArray(params.storeId) ? params.storeId.join(',') : params.storeId;
      queryParams = queryParams.set('storeId', storeIds);
    }
    if (params.startDate) {
      queryParams = queryParams.set('fromDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('toDate', params.endDate);
    }

    if (params.areaId) {
      const areaIds = Array.isArray(params.areaId) ? params.areaId.join(',') : params.areaId;
      queryParams = queryParams.set('areaId', areaIds);
    }
    if (params.stateId) {
      const stateIds = Array.isArray(params.stateId) ? params.stateId.join(',') : params.stateId;
      queryParams = queryParams.set('stateId', stateIds);
    }
    if (params.userId) {
      const userIds = Array.isArray(params.userId) ? params.userId.join(',') : params.userId;
      queryParams = queryParams.set('userId', userIds);
    }
    if (params.starProduct) {
      const starProduct = Array.isArray(params.starProduct)
        ? params.starProduct.join(',')
        : params.starProduct;
      queryParams = queryParams.set('starProduct', starProduct);
    }
    if (params.channelType) {
      const channelType = Array.isArray(params.channelType)
        ? params.channelType.join(',')
        : params.channelType;
      queryParams = queryParams.set('channelType', channelType);
    }
    if (params.mdaSda) {
      queryParams = queryParams.set('mdaSda', params.mdaSda);
    }
    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }
    if (params.projectNameId) {
      queryParams = queryParams.set('projectNameId', params.projectNameId);
    }
    if (params.onlyZeroPrice !== undefined && params.onlyZeroPrice !== null) {
      queryParams = queryParams.set('onlyZeroPrice', params.onlyZeroPrice.toString());
    }
    if (params.mdaSdaName) {
      queryParams = queryParams.set('mdaSdaName', params.mdaSdaName);
    }
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/selloutBundleOffer`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getAllPromoterShare(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    country?: string,
    account?: string | number | (string | number)[],
    channel?: string | number | (string | number)[],
    startDate?: string,
    endDate?: string,
    areaId?: number | string,
    stateId?: number | string
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    // Always set country parameter - component should handle the logic of passing all countries or selected countries
    if (params.country !== undefined && params.country !== null && params.country !== '') {
      queryParams = queryParams.set('country', params.country);
    }

    if (params.account) {
      let accountValue = params.account;
      if (Array.isArray(accountValue)) {
        accountValue = accountValue.join(',');
      }
      queryParams = queryParams.set('account', accountValue);
    }

    if (params.channel) {
      let channelValue = params.channel;
      if (Array.isArray(channelValue)) {
        channelValue = channelValue.join(',');
      }
      queryParams = queryParams.set('channel', channelValue);
    }

    if (params.startDate) {
      queryParams = queryParams.set('fromDate', params.startDate);
    }

    if (params.endDate) {
      queryParams = queryParams.set('toDate', params.endDate);
    }
    if (params.areaId) {
      const areaIds = Array.isArray(params.areaId) ? params.areaId.join(',') : params.areaId;
      queryParams = queryParams.set('areaId', areaIds);
    }
    if (params.stateId) {
      const stateIds = Array.isArray(params.stateId) ? params.stateId.join(',') : params.stateId;
      queryParams = queryParams.set('stateId', stateIds);
    }

    return this.http
      .get<any>(
        `${this.RAWDATA_API}/rawData/getAllPromoterShare`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllPromoterShareRaw(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    country?: number,
    account?: string | number | (string | number)[],
    channel?: string | number | (string | number)[],
    brand?: string | number | (string | number)[],
    promoterType?: number,
    startDate?: string,
    endDate?: string,
    storeId?: string | number | (string | number)[],  // Updated type to support multiple store IDs
    areaId?: number | string,
    stateId?: number | string,
    userId?: string | number | (string | number)[],
    focusStore?: string,
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    // Always set country parameter - component should handle the logic of passing all countries or selected countries
    if (params.country !== undefined && params.country !== null && params.country !== 0) {
      queryParams = queryParams.set('country', params.country);
    }

    if (params.account) {
      let accountValue = params.account;
      if (Array.isArray(accountValue)) {
        accountValue = accountValue.join(',');
      }
      queryParams = queryParams.set('account', accountValue);
    }

    if (params.channel) {
      let channelValue = params.channel;
      if (Array.isArray(channelValue)) {
        channelValue = channelValue.join(',');
      }
      queryParams = queryParams.set('channel', channelValue);
    }

    if (params.brand) {
      let brandValue = params.brand;
      if (Array.isArray(brandValue)) {
        brandValue = brandValue.join(',');
      }
      queryParams = queryParams.set('brandId', brandValue);
    }

    if (params.promoterType) {
      queryParams = queryParams.set('promoterTypeId', params.promoterType);
    }
    if (params.startDate) {
      queryParams = queryParams.set('fromDate', params.startDate);
    }

    if (params.endDate) {
      queryParams = queryParams.set('toDate', params.endDate);
    }

    if (params.storeId) {
      let storeValue = params.storeId;
      if (Array.isArray(storeValue)) {
        storeValue = storeValue.join(',');
      }
      queryParams = queryParams.set('storeId', storeValue);
    }
    if (params.areaId) {
      const areaIds = Array.isArray(params.areaId) ? params.areaId.join(',') : params.areaId;
      queryParams = queryParams.set('areaId', areaIds);
    }
    if (params.stateId) {
      const stateIds = Array.isArray(params.stateId) ? params.stateId.join(',') : params.stateId;
      queryParams = queryParams.set('stateId', stateIds);
    }
    if (params.userId) {
      let userIdValue = params.userId;
      if (Array.isArray(userIdValue)) {
        userIdValue = userIdValue.join(',');
      }
      queryParams = queryParams.set('userId', userIdValue);
    }
    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }

    return this.http
      .get<any>(
        `${this.RAWDATA_API}/rawData/getAllPromoterShareRaw`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exprotPromoterShare(params: {
    startDate?: string,
    endDate?: string
    searchFilter?: string,
    country?: string,
    account?: string,
    channel?: string,
    areaId?: number | number[],
    stateId?: number | number[]
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.country) {
      queryParams = queryParams.set('country', params.country);
    }

    if (params.account) {
      queryParams = queryParams.set('account', params.account);
    }

    if (params.channel) {
      queryParams = queryParams.set('channel', params.channel);
    }

    if (params.startDate) {
      queryParams = queryParams.set('fromDate', params.startDate);
    }

    if (params.endDate) {
      queryParams = queryParams.set('toDate', params.endDate);
    }
    if (params.areaId) {
      const areaIds = Array.isArray(params.areaId) ? params.areaId.join(',') : params.areaId;
      queryParams = queryParams.set('areaId', areaIds.toString());
    }
    if (params.stateId) {
      const stateIds = Array.isArray(params.stateId) ? params.stateId.join(',') : params.stateId;
      queryParams = queryParams.set('stateId', stateIds.toString());
    }

    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/rawData/promotershare/downloadExcelReport`,
        null,
        {
          params: queryParams,
          responseType: 'blob' as 'json'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Get user list for promoter share filter
  public getUserListPromoterShare(countryId?: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/getAllPromoterShareRaw/getUserList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exprotPromoterShareRaw(params: {
    startDate?: string,
    endDate?: string
    searchFilter?: string,
    country?: string | number,
    account?: string,
    channel?: string,
    brand?: string | number,
    promoterType?: number,
    storeId?: string | number,
    areaId?: number | number[],
    stateId?: number | number[],
    userId?: string | number | (string | number)[],
    focusStore?: string,
  }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.country) {
      queryParams = queryParams.set('country', params.country);
    }

    if (params.account) {
      queryParams = queryParams.set('account', params.account);
    }

    if (params.channel) {
      queryParams = queryParams.set('channel', params.channel);
    }

    if (params.brand) {
      queryParams = queryParams.set('brandId', params.brand);
    }

    if (params.promoterType) {
      queryParams = queryParams.set('promoterTypeId', params.promoterType);
    }

    if (params.storeId) {
      queryParams = queryParams.set('storeId', params.storeId);
    }

    if (params.startDate) {
      queryParams = queryParams.set('fromDate', params.startDate);
    }

    if (params.endDate) {
      queryParams = queryParams.set('toDate', params.endDate);
    }
    if (params.areaId) {
      const areaIds = Array.isArray(params.areaId) ? params.areaId.join(',') : params.areaId;
      queryParams = queryParams.set('areaId', areaIds.toString());
    }
    if (params.stateId) {
      const stateIds = Array.isArray(params.stateId) ? params.stateId.join(',') : params.stateId;
      queryParams = queryParams.set('stateId', stateIds.toString());
    }
    if (params.userId) {
      let userIdValue = params.userId;
      if (Array.isArray(userIdValue)) {
        userIdValue = userIdValue.join(',');
      }
      queryParams = queryParams.set('userId', userIdValue.toString());
    }
    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }

    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/rawData/promotershareraw/downloadExcelReport`,
        null,
        {
          params: queryParams,
          responseType: 'blob' as 'json'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  private buildExportQueryParams(params: {
    fromDate?: any,
    toDate?: any,
    exportImageType?: any,
    productId?: any,
    TDSId?: any,
    categoryId?: any,
    subCategoryId?: any,
    countryId?: any,
    channelId?: any,
    accountId?: any,
    storeId?: any,
    areaId?: number[],
    stateId?: number[],
    userId?: number[],
    starProduct?: string,
    mdaSda?: string,
    focusStore?: string,
    projectNameId?: string,
    onlyZeroPrice?: boolean,
    channelType?: string
  }): HttpParams {
    let queryParams = new HttpParams();

    if (params.fromDate) queryParams = queryParams.set('fromDate', params.fromDate);
    if (params.toDate) queryParams = queryParams.set('toDate', params.toDate);
    if (params.exportImageType) queryParams = queryParams.set('exportImageType', params.exportImageType);
    if (params.productId) queryParams = queryParams.set('productId', params.productId);
    if (params.TDSId) queryParams = queryParams.set('TDSId', params.TDSId);
    if (params.categoryId) queryParams = queryParams.set('categoryId', params.categoryId);
    if (params.subCategoryId) queryParams = queryParams.set('subCategoryId', params.subCategoryId);
    if (params.countryId) queryParams = queryParams.set('countryId', params.countryId);
    if (params.channelId) queryParams = queryParams.set('channelId', params.channelId);
    if (params.accountId) queryParams = queryParams.set('accountId', params.accountId);
    if (params.storeId) queryParams = queryParams.set('storeId', params.storeId);
    if (params.areaId) queryParams = queryParams.set('areaId', params.areaId.join(','));
    if (params.stateId) queryParams = queryParams.set('stateId', params.stateId.join(','));
    if (params.userId && params.userId.length > 0) queryParams = queryParams.set('userId', params.userId.join(','));
    if (params.starProduct) {
      const starProduct = Array.isArray(params.starProduct)
        ? params.starProduct.join(',')
        : params.starProduct;
      queryParams = queryParams.set('starProduct', starProduct);
    }
    if (params.channelType) {
      const channelType = Array.isArray(params.channelType)
        ? params.channelType.join(',')
        : params.channelType;
      queryParams = queryParams.set('channelType', channelType);
    }
    if (params.mdaSda) queryParams = queryParams.set('mdaSda', params.mdaSda);
    if (params.focusStore) queryParams = queryParams.set('focusStore', params.focusStore);
    if (params.projectNameId) queryParams = queryParams.set('projectNameId', params.projectNameId);
    if (params.onlyZeroPrice !== undefined && params.onlyZeroPrice !== null) queryParams = queryParams.set('onlyZeroPrice', params.onlyZeroPrice.toString());
    return queryParams;
  }

  public exportSellout(
    params: {
      fromDate?: any,
      toDate?: any,
      productId?: any,
      exportImageType?: any,
      TDSId?: any,
      categoryId?: any,
      subCategoryId?: any,
      countryId?: any,
      channelId?: any,
      accountId?: any,
      storeId?: any
      areaId?: number[],
      stateId?: number[],
      userId?: number[],
      starProduct?: string,
      mdaSda?: string,
      focusStore?: string,
      projectNameId?: string,
      onlyZeroPrice?: boolean,
      channelType?: string
    },
    options: { deleted?: boolean } = {}
  ): Observable<Blob> {
    const queryParams = this.buildExportQueryParams(params);
    const url = options.deleted
      ? `${this.RAWDATA_API}/selloutExport/downloadExcelReport`
      : `${this.RAWDATA_API}/rawData/sellout/downloadExcelReport`;

    return this.http
      .post<Blob>(url, null, { params: queryParams, responseType: 'blob' as 'json' })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Sell-out / bundle invoice stream (JWT + country check on API). Use isBlob for gateway.
   */
  public downloadSelloutInvoiceStream(
    saleId: string,
    source: string,
    fileIndex: string,
    kind = 'sellout'
  ): Observable<HttpResponse<Blob>> {
    let params = new HttpParams()
      .set('saleId', saleId)
      .set('source', source || 'active')
      .set('fileIndex', fileIndex || '0')
      .set('kind', kind || 'sellout');
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http
      .get(`${this.RAWDATA_API}/rawData/media/sellout-invoice`, {
        params,
        observe: 'response',
        responseType: 'blob',
        headers,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadMarketActivityMediaStream(
    activityId: string,
    fileIndex: string,
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('saleId', activityId)
      .set('fileIndex', fileIndex || '0');
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http
      .get(`${this.RAWDATA_API}/rawData/media/market-activity`, {
        params,
        observe: 'response',
        responseType: 'blob',
        headers,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public selloutBundleOfferExcelReport(
    params: {
      fromDate?: any,
      toDate?: any,
      productId?: any,
      exportImageType?: any,
      TDSId?: any,
      categoryId?: any,
      subCategoryId?: any,
      countryId?: any,
      channelId?: any,
      accountId?: any,
      storeId?: any
      areaId?: number[],
      stateId?: number[],
      userId?: number[],
      starProduct?: string,
      mdaSda?: string,
      focusStore?: string,
      projectNameId?: string,
      onlyZeroPrice?: boolean,
      channelType?: string
    },
    options: { deleted?: boolean } = {}
  ): Observable<Blob> {
    const queryParams = this.buildExportQueryParams(params);
    const url = `${this.RAWDATA_API}/rawData/selloutBundleOffer/downloadExcelReport`;

    return this.http
      .post<Blob>(url, null, { params: queryParams, responseType: 'blob' as 'json' })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportSelloutData(params: any) {
    return this.exportSellout(params, { deleted: false });
  }
  public exportSelloutIsDeletedData(params: any) {
    return this.exportSellout(params, { deleted: true });
  }

  public selloutUserList(countryId?: number | number[]): Observable<any> {
    let queryParams = new HttpParams();

    if (countryId) {
      // Handle both single country ID and array of country IDs
      if (Array.isArray(countryId)) {
        queryParams = queryParams.set('countryId', countryId.join(','));
      } else {
        queryParams = queryParams.set('countryId', countryId.toString());
      }
    }

    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/sellout/userList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getDisplayPlanogramData(params: {
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    countryId?: number[],
    accountId?: number[],
    brandId?: number[],
    categoryId?: number[],
    channelId?: number[],
    displayTypeId?: number[],
    cityId?: number[],
    storeId?: number[],
    type?: string[],
    startDate?: string,
    endDate?: string,
    areaId?: number[],
    stateId?: number[],
    userId?: number[],
    focusStore?: string,
    projectNameId?: number[],
    starProduct?: number[] | string,
  }): Observable<any> {


    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }

    if (params.accountId && params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','));
    }

    if (params.brandId && params.brandId.length > 0) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }

    if (params.categoryId && params.categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }


    if (params.channelId && params.channelId.length > 0) {
      queryParams = queryParams.set('channelId', params.channelId.join(','));
    }

    if (params.cityId && params.cityId.length > 0) {
      queryParams = queryParams.set('cityId', params.cityId.join(','));
    }

    if (params.storeId && params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','));
    }

    if (params.displayTypeId && params.displayTypeId.length > 0) {
      queryParams = queryParams.set('displayTypeId', params.displayTypeId.join(','));
    }

    if (params.type) {
      queryParams = queryParams.set('type', params.type.join(','));
    }

    if (params.startDate) {
      queryParams = queryParams.set('fromDate', params.startDate);
    }

    if (params.endDate) {
      queryParams = queryParams.set('toDate', params.endDate);
    }

    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','));
    }

    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','));
    }

    if (params.userId && params.userId.length > 0) {
      queryParams = queryParams.set('userId', params.userId.join(','));
    }

    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }

    if (params.projectNameId && params.projectNameId.length > 0) {
      queryParams = queryParams.set('projectNameId', params.projectNameId.join(','));
    }

    if (params.starProduct) {
      const val = Array.isArray(params.starProduct) ? params.starProduct.join(',') : params.starProduct;
      queryParams = queryParams.set('starProduct', val);
    }

    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/displayPlanogram`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getUserListDisplayPlanogram(countryId: number | number[]): Observable<any> {
    let queryParams = new HttpParams();

    if (countryId) {
      // Handle both single country ID and array of country IDs
      if (Array.isArray(countryId)) {
        queryParams = queryParams.set('countryId', countryId.join(','));
      } else {
        queryParams = queryParams.set('countryId', countryId.toString());
      }
    }

    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/displayPlanogram/userList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportDisplayPlanogramData(filterValue: any): Observable<any> {

    let queryParams = new HttpParams()

    if (filterValue.fromDate) {
      queryParams = queryParams.set('fromDate', filterValue.fromDate);
    }

    if (filterValue.toDate) {
      queryParams = queryParams.set('toDate', filterValue.toDate);
    }

    if (filterValue.countryId && filterValue.countryId.length > 0) {
      queryParams = queryParams.set('countryId', filterValue.countryId.join(','));
    }

    if (filterValue.accountId && filterValue.accountId.length > 0) {
      queryParams = queryParams.set('accountId', filterValue.accountId.join(','));
    }


    if (filterValue.exportImageType) {
      queryParams = queryParams.set('exportImageType', filterValue.exportImageType);
    }

    if (filterValue.displayTypeId && filterValue.displayTypeId.length > 0) {
      queryParams = queryParams.set('displayTypeId', filterValue.displayTypeId.join(','));
    }

    if (filterValue.searchFilter) {
      queryParams = queryParams.set('searchFilter', filterValue.searchFilter);
    }

    if (filterValue.brandId && filterValue.brandId.length > 0) {
      queryParams = queryParams.set('brandId', filterValue.brandId.join(','));
    }

    if (filterValue.categoryId && filterValue.categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', filterValue.categoryId.join(','));
    }

    if (filterValue.channelId && filterValue.channelId.length > 0) {
      queryParams = queryParams.set('channelId', filterValue.channelId.join(','));
    }

    if (filterValue.cityId && filterValue.cityId.length > 0) {
      queryParams = queryParams.set('cityId', filterValue.cityId.join(','));
    }

    if (filterValue.storeId && filterValue.storeId.length > 0) {
      queryParams = queryParams.set('storeId', filterValue.storeId.join(','));
    }

    if (filterValue.type) {
      queryParams = queryParams.set('type', filterValue.type);
    }

    if (filterValue.areaId && filterValue.areaId.length > 0) {
      queryParams = queryParams.set('areaId', filterValue.areaId.join(','));
    }

    if (filterValue.stateId && filterValue.stateId.length > 0) {
      queryParams = queryParams.set('stateId', filterValue.stateId.join(','));
    }

    if (filterValue.userId && filterValue.userId.length > 0) {
      queryParams = queryParams.set('userId', filterValue.userId.join(','));
    }

    if (filterValue.focusStore) {
      queryParams = queryParams.set('focusStore', filterValue.focusStore);
    }

    if (filterValue.projectNameId && filterValue.projectNameId.length > 0) {
      queryParams = queryParams.set('projectNameId', filterValue.projectNameId.join(','));
    }

    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/rawData/displayPlanogram/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob' as 'json'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public cancelDisplayPlanogramExportJob(jobId: string): Observable<unknown> {
    return this.asyncExportService
      .cancelJob(this.displayPlanogramExportApi.apiBaseUrl, jobId)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportDisplayPlanogramAsync(
    params: Record<string, unknown>,
    progress: ExcelExportProgressSlot,
  ): Observable<void> {
    return this.asyncExportService.runAsyncExport(
      this.displayPlanogramExportApi,
      params,
      progress,
    );
  }

  public getDisplayShareSyncStatus(): Observable<{
    lastSyncTime: string | null;
    syncedBy: number | null;
  }> {
    return this.http
      .get<{ result?: { lastSyncTime: string | null; syncedBy: number | null } }>(
        `${this.RAWDATA_API}/rawData/displayShare/syncStatus`,
      )
      .pipe(
        map((r) => r?.result ?? { lastSyncTime: null, syncedBy: null }),
        catchError(this.errorHandler.handleError),
      );
  }

  public refreshDisplayShareData(): Observable<{
    accepted: boolean;
    inProgress: boolean;
    lastSyncTime: string | null;
  }> {
    return this.http
      .post<{
        result?: {
          accepted: boolean;
          inProgress: boolean;
          lastSyncTime: string | null;
        };
      }>(`${this.RAWDATA_API}/rawData/displayShare/refreshData`, {})
      .pipe(
        map(
          (r) =>
            r?.result ?? {
              accepted: false,
              inProgress: false,
              lastSyncTime: null,
            },
        ),
        catchError(this.errorHandler.handleError),
      );
  }

  public getDisplayPlanogramSyncStatus(): Observable<{
    lastSyncTime: string | null;
    syncedBy: number | null;
  }> {
    return this.http
      .get<{ result?: { lastSyncTime: string | null; syncedBy: number | null } }>(
        `${this.RAWDATA_API}/rawData/displayPlanogram/syncStatus`,
      )
      .pipe(
        map((r) => r?.result ?? { lastSyncTime: null, syncedBy: null }),
        catchError(this.errorHandler.handleError),
      );
  }

  public getPromoterShareSyncStatus(): Observable<{
    lastSyncTime: string | null;
    syncedBy: number | null;
  }> {
    return this.http
      .get<{ result?: { lastSyncTime: string | null; syncedBy: number | null } }>(
        `${this.RAWDATA_API}/rawData/promoterShare/syncStatus`,
      )
      .pipe(
        map((r) => r?.result ?? { lastSyncTime: null, syncedBy: null }),
        catchError(this.errorHandler.handleError),
      );
  }

  public refreshPromoterShareData(): Observable<{
    accepted: boolean;
    inProgress: boolean;
    lastSyncTime: string | null;
  }> {
    return this.http
      .post<{
        result?: {
          accepted: boolean;
          inProgress: boolean;
          lastSyncTime: string | null;
        };
      }>(`${this.RAWDATA_API}/rawData/promoterShare/refreshData`, {})
      .pipe(
        map(
          (r) =>
            r?.result ?? {
              accepted: false,
              inProgress: false,
              lastSyncTime: null,
            },
        ),
        catchError(this.errorHandler.handleError),
      );
  }

  public refreshDisplayPlanogramData(): Observable<{
    accepted: boolean;
    inProgress: boolean;
    lastSyncTime: string | null;
  }> {
    return this.http
      .post<{
        result?: {
          accepted: boolean;
          inProgress: boolean;
          lastSyncTime: string | null;
        };
      }>(`${this.RAWDATA_API}/rawData/displayPlanogram/refreshData`, {})
      .pipe(
        map(
          (r) =>
            r?.result ?? {
              accepted: false,
              inProgress: false,
              lastSyncTime: null,
            },
        ),
        catchError(this.errorHandler.handleError),
      );
  }

  public getDisplayShareData(params: {
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    startDate?: string,
    endDate?: string,
    countryId?: string,
    brandId?: string,
    categoryId?: string,
    subCategoryId?: string,
    segmentId?: string,
    channelId?: string,
    accountId?: string,
    storeId?: number,
    stateId?: string,
    areaId?: string,
    userId?: string,
    focusStore?: string,
    projectNameId?: string,
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }

    if (params.brandId) {
      queryParams = queryParams.set('brandId', params.brandId);
    }

    if (params.categoryId) {
      queryParams = queryParams.set('categoryId', params.categoryId);
    }

    if (params.subCategoryId) {
      queryParams = queryParams.set('subCategoryId', params.subCategoryId);
    }

    if (params.segmentId) {
      queryParams = queryParams.set('segmentId', params.segmentId);
    }

    if (params.accountId) {
      queryParams = queryParams.set('accountId', params.accountId);
    }

    if (params.channelId) {
      queryParams = queryParams.set('channelId', params.channelId);
    }

    if (params.storeId) {
      queryParams = queryParams.set('storeId', params.storeId);
    }

    if (params.stateId) {
      queryParams = queryParams.set('stateId', params.stateId);
    }

    if (params.areaId) {
      queryParams = queryParams.set('areaId', params.areaId);
    }

    if (params.userId) {
      queryParams = queryParams.set('userId', params.userId);
    }

    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }

    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }

    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }

    if (params.projectNameId) {
      queryParams = queryParams.set('projectNameId', params.projectNameId);
    }

    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/displayShare`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getUserListDisplayShare(searchFilter: string, countryId: number | number[]): Observable<any> {
    let queryParams = new HttpParams();

    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }

    if (countryId) {
      // Handle both single number and array
      if (Array.isArray(countryId)) {
        queryParams = queryParams.set('countryId', countryId.join(','));
      } else {
        queryParams = queryParams.set('countryId', countryId.toString());
      }
    }

    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/displayShare/getUserList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayShareByStoreData(params: {
    pageIndex: number,
    pageSize: number,
    searchFilter?: string,
    startDate?: string,
    endDate?: string,
    countryId?: string,
    // brandId?: number,
    // categoryId?: number,
    // subCategoryId?: number,
    // segmentId?: number,
    channelId?: string,
    accountId?: string,
    // cityId?: number
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }

    // if (params.brandId) {
    //   queryParams = queryParams.set('brandId', params.brandId);
    // }

    // if (params.categoryId) {
    //   queryParams = queryParams.set('categoryId', params.categoryId);
    // }

    // if (params.subCategoryId) {
    //   queryParams = queryParams.set('subCategoryId', params.subCategoryId);
    // }

    // if (params.segmentId) {
    //   queryParams = queryParams.set('segmentId', params.segmentId);
    // }

    if (params.accountId) {
      queryParams = queryParams.set('accountId', params.accountId);
    }

    if (params.channelId) {
      queryParams = queryParams.set('channelId', params.channelId);
    }

    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }

    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }

    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/displayShareByStore`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //export display share data
  public exportDisplayShare(params: {
    searchFilter?: string,
    month?: number,
    year?: number,
    countryId?: number | string,
    brandId?: string,
    categoryId?: string,
    subCategoryId?: string,
    segmentId?: string,
    channelId?: string,
    accountId?: string,
    storeId?: number,
    startDate?: string,
    endDate?: string,
    areaId?: number[],
    stateId?: number[],
    userId?: number[],
    focusStore?: string,
    projectNameId?: string,
  }): Observable<any> {
    let queryParams = new HttpParams()
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryId != null && params.countryId !== '') {
      queryParams = queryParams.set('countryId', String(params.countryId));
    }

    if (params.storeId) {
      queryParams = queryParams.set('storeId', params.storeId);
    }

    if (params.brandId) {
      queryParams = queryParams.set('brandId', params.brandId);
    }

    if (params.categoryId) {
      queryParams = queryParams.set('categoryId', params.categoryId);
    }

    if (params.subCategoryId) {
      queryParams = queryParams.set('subCategoryId', params.subCategoryId);
    }

    if (params.segmentId) {
      queryParams = queryParams.set('segmentId', params.segmentId);
    }

    if (params.accountId) {
      queryParams = queryParams.set('accountId', params.accountId);
    }

    if (params.channelId) {
      queryParams = queryParams.set('channelId', params.channelId);
    }

    if (params.month) {
      queryParams = queryParams.set('month', params.month);
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year);
    }

    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }

    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }

    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','));
    }

    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','));
    }

    if (params.userId && params.userId.length > 0) {
      queryParams = queryParams.set('userId', params.userId.join(','));
    }

    if (params.focusStore) {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }

    if (params.projectNameId) {
      queryParams = queryParams.set('projectNameId', params.projectNameId);
    }

    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/rawData/displayShare/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob' as 'json'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public cancelDisplayShareExportJob(jobId: string): Observable<unknown> {
    return this.asyncExportService
      .cancelJob(this.displayShareExportApi.apiBaseUrl, jobId)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportDisplayShareAsync(
    params: {
      searchFilter?: string;
      month?: number;
      year?: number;
      countryId?: number | string;
      brandId?: string;
      categoryId?: string;
      subCategoryId?: string;
      segmentId?: string;
      channelId?: string;
      accountId?: string;
      storeId?: number;
      startDate?: string;
      endDate?: string;
      areaId?: number[];
      stateId?: number[];
      userId?: number[];
    },
    progress: ExcelExportProgressSlot,
  ): Observable<void> {
    return this.asyncExportService.runAsyncExport(
      this.displayShareExportApi,
      params,
      progress,
    );
  }

  //export display share data
  public exportDisplayShareByStore(params: {
    searchFilter?: string,
    month?: number,
    year?: number,
    countryId?: number,
    // brandId?: number,
    // categoryId?: number,
    // subCategoryId?: number,
    // segmentId?: number,
    channelId?: string,
    accountId?: string,
    startDate?: string,
    endDate?: string
  }): Observable<any> {
    let queryParams = new HttpParams()
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }

    if (params.accountId) {
      queryParams = queryParams.set('accountId', params.accountId);
    }

    if (params.channelId) {
      queryParams = queryParams.set('channelId', params.channelId);
    }

    if (params.month) {
      queryParams = queryParams.set('month', params.month);
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year);
    }

    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }

    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }

    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/rawData/displayShareByStore/downloadExcelReport`,
        {},
        {
          params: queryParams,
          responseType: 'blob' as 'json'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getStoreUpdatedCountByMonth(params: {
    month?: number,
    year?: number
  }): Observable<any> {
    let queryParams = new HttpParams()
    if (params.month) {
      queryParams = queryParams.set('month', params.month);
    }

    if (params.year) {
      queryParams = queryParams.set('year', params.year);
    }
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/getStoreUpdatedCountByMonth`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayShareByBrand(countryId: number[], categoryId: number[], accountId: number[], subCategoryId: number[], year: number, areaId?: number[], stateId?: number[], storeId?: number[]): Observable<any> {
    let queryParams = new HttpParams();

    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    if (categoryId && categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', categoryId.join(','));
    }
    if (accountId && accountId.length > 0) {
      queryParams = queryParams.set('accountId', accountId.join(','));
    }
    if (subCategoryId && subCategoryId.length > 0) {
      queryParams = queryParams.set('subCategoryId', subCategoryId.join(','));
    }
    if (year) {
      queryParams = queryParams.set('year', year);
    }
    if (areaId && areaId.length > 0) {
      queryParams = queryParams.set('areaId', areaId.join(','));
    }
    if (stateId && stateId.length > 0) {
      queryParams = queryParams.set('stateId', stateId.join(','));
    }
    if (storeId && storeId.length > 0) {
      queryParams = queryParams.set('storeId', storeId.join(','));
    }
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/displayShareByBrand`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayCountByBrandDashboard(countryId: number[], categoryId: number[], accountId: number[], subCategoryId: number[], year: number, areaId?: number[], stateId?: number[], storeId?: number[]): Observable<any> {
    let queryParams = new HttpParams();

    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    if (categoryId && categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', categoryId.join(','));
    }
    if (accountId && accountId.length > 0) {
      queryParams = queryParams.set('accountId', accountId.join(','));
    }
    if (subCategoryId && subCategoryId.length > 0) {
      queryParams = queryParams.set('subCategoryId', subCategoryId.join(','));
    }
    if (year) {
      queryParams = queryParams.set('year', year);
    }
    if (areaId && areaId.length > 0) {
      queryParams = queryParams.set('areaId', areaId.join(','));
    }
    if (stateId && stateId.length > 0) {
      queryParams = queryParams.set('stateId', stateId.join(','));
    }
    if (storeId && storeId.length > 0) {
      queryParams = queryParams.set('storeId', storeId.join(','));
    }

    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/displayCountByBrandDashboard`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayMatrixByAccCatScat(accountId: number[], categoryId: number, subCategoryId: number[], from: string, to: string, storeId: number[], countryId: number[], pageIndex?: number, pageSize?: number, withImage?: string, areaId?: number[], stateId?: number[], brandId?: number[]): Observable<any> {
    let queryParams = new HttpParams()
    // .set('accountId', accountId ? accountId.join(',') : 0)
    // .set('categoryId', categoryId)
    // .set('subCategoryId', Array.isArray(subCategoryId) ? subCategoryId.join(',') : subCategoryId)

    if (storeId && storeId.length > 0) {
      queryParams = queryParams.set('storeId', storeId.join(','));
    }
    if (categoryId) {
      queryParams = queryParams.set('categoryId', categoryId);
    }
    if (accountId && accountId.length > 0) {
      queryParams = queryParams.set('accountId', accountId.join(','));
    }
    if (subCategoryId && subCategoryId.length > 0) {
      queryParams = queryParams.set('subCategoryId', subCategoryId.join(','));
    }

    if (from) {
      queryParams = queryParams.set('fromDate', from);
    }

    if (to) {
      queryParams = queryParams.set('toDate', to);
    }

    if (pageIndex) {
      queryParams = queryParams.set('pageIndex', pageIndex);
    }

    if (pageSize) {
      queryParams = queryParams.set('pageSize', pageSize);
    }

    if (withImage) {
      queryParams = queryParams.set('withImage', withImage);
    }

    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    if (areaId && areaId.length > 0) {
      queryParams = queryParams.set('areaId', areaId.join(','));
    }
    if (stateId && stateId.length > 0) {
      queryParams = queryParams.set('stateId', stateId.join(','));
    }
    if (brandId && brandId.length > 0) {
      queryParams = queryParams.set('brandId', brandId.join(','));
    }
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/displayMatrixByAccCatScat`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayMatrixByStoreId(storeId: number, pageIndex?: number, pageSize?: number): Observable<any> {
    let queryParams = new HttpParams()


    if (storeId) {
      queryParams = queryParams.set('storeId', storeId);
    }

    if (pageIndex) {
      queryParams = queryParams.set('pageIndex', pageIndex);
    }

    if (pageSize) {
      queryParams = queryParams.set('pageSize', pageSize);
    }
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/displayMatrixByStoreId`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayComplianceByAccCatScat(
    accountId: number[],
    categoryId: number | number[],
    subCategoryId: number[],
    from: string,
    to: string,
    storeId: number[],
    countryId: number[],
    pageIndex?: number,
    pageSize?: number,
    withImage?: string,
    areaId?: number[],
    stateId?: number[],
    brandId?: number[],
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (storeId?.length) {
      queryParams = queryParams.set('storeId', storeId.join(','));
    }
    const categoryIds = Array.isArray(categoryId) ? categoryId : [categoryId];
    if (categoryIds.length) {
      queryParams = queryParams.set('categoryId', categoryIds.join(','));
    }
    if (accountId?.length) {
      queryParams = queryParams.set('accountId', accountId.join(','));
    }
    if (subCategoryId?.length) {
      queryParams = queryParams.set('subCategoryId', subCategoryId.join(','));
    }
    if (from) {
      queryParams = queryParams.set('fromDate', from);
    }
    if (to) {
      queryParams = queryParams.set('toDate', to);
    }
    if (pageIndex) {
      queryParams = queryParams.set('pageIndex', pageIndex);
    }
    if (pageSize) {
      queryParams = queryParams.set('pageSize', pageSize);
    }
    if (withImage) {
      queryParams = queryParams.set('withImage', withImage);
    }
    if (countryId?.length) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    if (areaId?.length) {
      queryParams = queryParams.set('areaId', areaId.join(','));
    }
    if (stateId?.length) {
      queryParams = queryParams.set('stateId', stateId.join(','));
    }
    if (brandId?.length) {
      queryParams = queryParams.set('brandId', brandId.join(','));
    }
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/displayComplianceByAccCatScat`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayComplianceByStoreId(
    storeId: number,
    pageIndex?: number,
    pageSize?: number,
  ): Observable<any> {
    let queryParams = new HttpParams();
    if (storeId) {
      queryParams = queryParams.set('storeId', storeId);
    }
    if (pageIndex) {
      queryParams = queryParams.set('pageIndex', pageIndex);
    }
    if (pageSize) {
      queryParams = queryParams.set('pageSize', pageSize);
    }
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/displayComplianceByStoreId`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public fetchAllFixtureShareTypesList(term?: string, countryId?: number[], month?: string, accountId?: number[], storeId?: number[], brandId?: number[], areaId?: number[], stateId?: number[]): Observable<any> {

    let queryParams = new HttpParams()
    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryId', countryId.join(','));
    }
    if (month) {
      queryParams = queryParams.set('month', month);
    }
    if (term) {
      queryParams = queryParams.set('searchFilter', term);
    }
    if (accountId && accountId.length > 0) {
      queryParams = queryParams.set('accountId', accountId.join(','));
    }
    if (storeId && storeId.length > 0) {
      queryParams = queryParams.set('storeId', storeId.join(','));
    }
    if (brandId && brandId.length > 0) {
      queryParams = queryParams.set('brandId', brandId.join(','));
    }
    if (areaId && areaId.length > 0) {
      queryParams = queryParams.set('areaId', areaId.join(','));
    }
    if (stateId && stateId.length > 0) {
      queryParams = queryParams.set('stateId', stateId.join(','));
    }


    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/fixtureshareTypeList`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDisplayTypeByCountry(countryIds: number[]): Observable<any> {
    let queryParams = new HttpParams();
    if (countryIds && countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', countryIds.join(','));
    }
    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/getDisplayTypeByCountry`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get category data based on summary page Fixture Share Type (Display type) selection.
   */
  public fetchCategoriesByDisplayType(params: {
    displayTypeId: number[];
    countryId?: number[];
    month?: string;
    accountId?: number[];
    storeId?: number[];
    brandId?: number[];
    areaId?: number[];
    stateId?: number[];
  }): Observable<{ id: number; name: string }[]> {
    let queryParams = new HttpParams();
    if (params.displayTypeId && params.displayTypeId.length > 0) {
      queryParams = queryParams.set('displayTypeId', params.displayTypeId.join(','));
    }
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month);
    }
    if (params.accountId && params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','));
    }
    if (params.storeId && params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','));
    }
    if (params.brandId && params.brandId.length > 0) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }
    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','));
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','));
    }
    return this.http
      .get<{ id: number; name: string }[]>(`${this.RAWDATA_API}/rawData/categoriesByDisplayType`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public fetchAllDisplayTypesList(params: { countryId: number[], month: string, accountId: number[], storeId: number[], areaId?: number[], stateId?: number[], categoryId?: number[], subCategoryId?: number[] }): Observable<any> {
    let queryParams = new HttpParams();

    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month);
    }
    if (params.accountId && params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','));
    }
    if (params.storeId && params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','));
    }
    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','));
    }
    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','));
    }
    if (params.categoryId && params.categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }
    if (params.subCategoryId && params.subCategoryId.length > 0) {
      queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
    }
    // if (params.channelId) {
    //   queryParams = queryParams.set('channelId', params.channelId);
    // }

    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/fixtureshareDisplayTypesQunatity`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public fetchAllFixtureImageDetails(params: {
    countryId: number[],
    month: string,
    displayType: number[],
    brand: number[],
    accountId: number[],
    storeId: number[],
    pageIndex?: number,
    pageSize?: number,
    sortOrder?: string,
    areaId?: number[],
    stateId?: number[],
    categoryId?: number[],
    subCategoryId?: number[]
  }): Observable<any> {
    console.log(params);

    let queryParams = new HttpParams()
    if (params.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }
    if (params.month) {
      queryParams = queryParams.set('month', params.month);
    }

    if (params.displayType && params.displayType.length > 0) {
      queryParams = queryParams.set('displayType', params.displayType.join(','));
    }

    if (params.brand && params.brand.length > 0) {
      queryParams = queryParams.set('brandId', params.brand.join(','));
    }
    if (params.accountId && params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','));
    }
    if (params.storeId && params.storeId.length > 0) {
      queryParams = queryParams.set('storeId', params.storeId.join(','));
    }

    if (params.categoryId && params.categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }

    if (params.subCategoryId && params.subCategoryId.length > 0) {
      queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
    }

    if (params.pageIndex) {
      queryParams = queryParams.set('pageIndex', params.pageIndex);
    }

    if (params.pageSize) {
      queryParams = queryParams.set('pageSize', params.pageSize);
    }

    if (params.sortOrder) {
      queryParams = queryParams.set('sortOrder', params.sortOrder);
    }

    if (params.areaId && params.areaId.length > 0) {
      queryParams = queryParams.set('areaId', params.areaId.join(','));
    }

    if (params.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','));
    }

    return this.http
      .get<any>(`${this.RAWDATA_API}/rawData/fixtureShareImages`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Master User Import/Export APIs
  public downloadMasterUserExcelTemplate(roles: string, countryId: number | number[]): Observable<Blob> {
    let queryParams = new HttpParams();
    if (roles) {
      queryParams = queryParams.set('roles', roles);
    }
    const countryArray = Array.isArray(countryId) ? countryId : [countryId];

    if (countryArray.length > 0) {
      queryParams = queryParams.set('countryId', countryArray.join(','));
    }

    return this.http
      .get<Blob>(`${this.RAWDATA_API}/masterUserImportExport/v2/downloadExcelTemplate`, {
        params: queryParams,
        responseType: 'blob' as 'json'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importMasterUserData(users: any[], countryId?: number): Observable<any> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .post<any>(`${this.RAWDATA_API}/masterUserImportExport/importData`, users, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public startImportSession(): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this.RAWDATA_API}/masterUserImportExport/startImportSession`, {})
      .pipe(catchError(this.errorHandler.handleError));
  }

  public validateAllUsers(users: any[], countryId?: number): Observable<{ message: string, totalUsers: number, batchInfo: any }> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .post<{ message: string, totalUsers: number, batchInfo: any }>(`${this.RAWDATA_API}/masterUserImportExport/validateAllUsers`, {
        users
      }, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importBatch(users: any[], batchNumber: number, totalBatches: number, countryId?: number): Observable<{ message: string, processedCount: number, batchNumber: number, totalBatches: number }> {
    let params = new HttpParams();
    if (countryId) {
      params = params.set('countryId', countryId.toString());
    }
    return this.http
      .post<{ message: string, processedCount: number, batchNumber: number, totalBatches: number }>(`${this.RAWDATA_API}/masterUserImportExport/importBatch`, {
        users,
        batchNumber,
        totalBatches
      }, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }


  //download excel template product
  public downloadExcelTemplate(countryId?: number) {
    let params: any = { responseType: 'blob' as 'json' };
    if (countryId) {
      params.params = { countryId };
    }
    return this.http
      .get(`${this.RAWDATA_API}/productImportExport/downloadExcelTemplate`, params)
      .pipe(catchError(this.errorHandler.handleError));
  }


  //import product data
  public importProduct(data: Record<string, any>): Observable<any[]> {
    return this.http
      .post<any[]>(`${this.RAWDATA_API}/productImportExport/importData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }


  //To check the dropdown validation for product import
  public dropdownDataCheck(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/productImportExport/dropdownDataCheck`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To check the valid pnc code and model for product import
  public checkValidPNCAndModel(rows: Array<{ index: number, pncCode?: string, model?: string, country?: string }>): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/productImportExport/checkValidPNCAndModel`, rows)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // to check the valid pnc code and model for schedule gross price import
  public checkValidPNCAndModelForScheduleGrossPrice(existingPncCode: string[], existingModel: string[], countryId: number): Observable<any> {
    let data = { existingPncCode, existingModel, countryId }
    return this.http
      .post<any>(`${this.RAWDATA_API}/productImportExport/checkValidPNCAndModelForScheduleGrossPrice`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //export product table data
  public exportProduct(params?: {
    searchFilter?: string,
    countryId?: number[],
    categoryId?: number[],
    subCategoryId?: number[],
    brandId?: number[],
    series?: string[],
    status?: number[],
    source?: string,
    projectNameId?: number[],
  }): Observable<Blob> {
    let queryParams = new HttpParams()
    if (params?.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params?.countryId && params?.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }

    if (params?.categoryId && params?.categoryId.length > 0) {
      queryParams = queryParams.set('categoryId', params.categoryId.join(','));
    }

    if (params?.subCategoryId && params?.subCategoryId.length > 0) {
      queryParams = queryParams.set('subCategoryId', params.subCategoryId.join(','));
    }
    if (params?.brandId && params?.brandId.length > 0) {
      queryParams = queryParams.set('brandId', params.brandId.join(','));
    }

    if (params?.series && params?.series.length > 0) {
      queryParams = queryParams.set('series', params.series.join(','));
    }

    if (params?.status && params?.status.length > 0) {
      queryParams = queryParams.set('status', params.status.join(','));
    }

    if (params?.source) {
      queryParams = queryParams.set('source', params.source);
    }

    if (params?.projectNameId && params.projectNameId.length > 0) {
      queryParams = queryParams.set(
        'projectNameId',
        params.projectNameId.join(','),
      );
    }

    const columns: string[] = [
      'PNC',
      'Model',
      'Segment',
      'Category',
      'Sub Category',
      'Brand',
      'MDA/SDA',
      'FS/BI',
      'Product Name',
      'Project Name',
      'Series',
      'Country',
      'Product Line',
      'Price',
      'Product Tier',
      'Channel Type',
      'Price Promotion',
      'Source',
      'Product Status',
      'Status',
      'Created By',
      'Created Date & Time',
      'Updated By',
      'Updated Date & Time',
    ]; //export needed columns
    // const columns:string[] = []; //export all columns
    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/productImportExport/downloadExcelReport`,
        { columns },
        {
          params: queryParams,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  //To download the product import template
  public excelDownloadProductLine() {
    return this.http
      .get(`${this.RAWDATA_API}/productImportExport/downloadExcelTemplate`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }


  //to download the excel template to upload a store
  public downloadExcelStoreTemplate(countryId?: number[]) {
    let params: any = { responseType: 'blob' as 'json' };
    if (countryId) {
      params.params = { countryId };
    }
    return this.http
      .get(`${this.RAWDATA_API}/storeImportExport/downloadExcelTemplate`, params)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // **
  //  * Imports a list of stores into the server
  public importStoresData(data: any): Observable<Store[]> {
    return this.http
      .post<Store[]>(`${this.RAWDATA_API}/storeImportExport/importStoreData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }


  //To check the unique store id for import store and validate delete operations
  public checkValidStoreIdAndName(existingStoreId: number[], existingName: string[], deleteStoreIds?: number[], countryId?: number | number[]): Observable<any> {
    // Ensure countryId is sent as an array if provided
    const countryIdArray = countryId !== undefined
      ? (Array.isArray(countryId) ? countryId : [countryId])
      : undefined;

    let data = {
      existingStoreId,
      existingName,
      deleteStoreIds: deleteStoreIds || [],
      countryId: countryIdArray
    }
    return this.http
      .post<any>(`${this.RAWDATA_API}/storeImportExport/checkValidStoreIdAndName`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To check the dropdown validation for store import
  public storeDropDownCheck(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/storeImportExport/storeDropDownCheck`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }


  //to export the store data from the table
  public exportStores(params?: {
    searchFilter?: string,
    countryId?: number[],
    stateId?: number[],
    cityId?: number[],
    channelId?: number[],
    accountId?: number[],
    status?: number,
    withPromoter?: string,
    focusStore?: string
  }): Observable<Blob> {

    let queryParams = new HttpParams()
    if (params?.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params?.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }

    if (params?.stateId && params.stateId.length > 0) {
      queryParams = queryParams.set('stateId', params.stateId.join(','));
    }

    if (params?.cityId && params.cityId.length > 0) {
      queryParams = queryParams.set('cityId', params.cityId.join(','));
    }

    if (params?.accountId && params.accountId.length > 0) {
      queryParams = queryParams.set('accountId', params.accountId.join(','));
    }

    if (params?.channelId && params.channelId.length > 0) {
      queryParams = queryParams.set('channelId', params.channelId.join(','));
    }

    if (params?.status !== undefined) {
      queryParams = queryParams.set('status', params?.status);
    }

    if (params?.withPromoter && params.withPromoter !== 'ALL') {
      queryParams = queryParams.set('withPromoter', params.withPromoter);
    }

    if (params?.focusStore && params.focusStore !== 'ALL') {
      queryParams = queryParams.set('focusStore', params.focusStore);
    }

    const columns: string[] = [
      'Store ID',
      'Store Name',
      'Dealer Name',
      'Dealer Code',
      'Channel',
      'Account',
      'Region',
      'Country',
      'AreaGroup',
      'State',
      'City',
      'Store Address',
      'Telephone Number',
      'Location Longitude',
      'Location Latitude',
      'Contact Person',
      'Email',
      'With Promoter',
      'Focus Store',
      'Store Grade',
      'Online/Offline',
      'Store Code',
      'Tier',
      'SAP Code',
      'Created By',
      'Created Date & Time',
      'Updated By',
      'Updated Date Time',
    ]; //export needed columns
    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/storeImportExport/downloadExcelReport`,
        { columns },
        {
          params: queryParams,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //To export the master user table data
  public exportUsers(
    searchValue: string,
    userStatus: number | null | undefined,
    countryIds?: number[],
    regionIds?: number[],
    userRoleId?: number[],
    userType?: number[],
    projectId?: number | null
  ): Observable<Blob> {
    // return this.exportData('user', );
    let columns = [
      'Name',
      'Login ID',
      'Email',
      'Phone Number',
      'DOB',
      'Gender',
      'Role',
      'Reporting officer',
      'Tier',
      'Promoter Category',
      'Batch / Group',
      'Test User',
      'Joining Date',
      'Incentive Scheme',
      'Country',
      'Leave Approval',
      'Created By',
      'Created Date & Time',
      'Updated By',
      'Updated Date Time',
    ]
    const payload: any = {
      columns,
      searchValue,
      countryIds,
      regionIds,
      userRoleId,
      userType,
      projectId,
    };

    if (userStatus !== null && userStatus !== undefined) {
      payload.userStatus = userStatus;
    }

    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/masterUserImportExport/downloadExcelReport`,
        payload,
        { responseType: 'blob' as 'json' }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  // setup price mapping template download
  public downloadSetupPriceMappingTemplate(countryId: number[], accountId: number[]): Observable<Blob> {
    let queryParams = new HttpParams();

    if (countryId && countryId.length > 0) {
      queryParams = queryParams.set('countryIds', countryId.join(','));
    }
    if (accountId && accountId.length > 0) {
      queryParams = queryParams.set('accountIds', accountId.join(','));
    }

    return this.http
      .get<Blob>(`${this.RAWDATA_API}/PriceMappingImportExport/downloadExcelTemplate`, {
        params: queryParams,
        responseType: 'blob' as 'json'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // setup price mapping template upload
  public uploadSetupPriceMapping(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/PriceMappingImportExport/uploadSetupPriceMapping`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // user store mapping template download
  public downloadUserStoreMappingExcelTemplate(
    countryId?: number,
    pageIndex?: number,
    pageSize?: number,
    accountIds?: number[],
    roleId?: number,
    searchValue?: string,
    userId?: number,
    promoterTypeId?: number
  ): Observable<Blob> {
    let queryParams = new HttpParams();

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    if (pageIndex != null && pageIndex > 0) {
      queryParams = queryParams.set('pageIndex', String(pageIndex));
    }
    if (pageSize != null && pageSize > 0) {
      queryParams = queryParams.set('pageSize', String(pageSize));
    }
    if (accountIds && accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', accountIds.join(','));
    }
    if (roleId) {
      queryParams = queryParams.set('roleId', String(roleId));
    }
    if (searchValue) {
      queryParams = queryParams.set('searchValue', searchValue);
    }
    if (userId) {
      queryParams = queryParams.set('userId', String(userId));
    }
    if (promoterTypeId) {
      queryParams = queryParams.set('promoterTypeId', String(promoterTypeId));
    }
    return this.http
      .get<Blob>(`${this.RAWDATA_API}/userStoreMappingImportExport/downloadExcelTemplate`, {
        params: queryParams,
        responseType: 'blob' as 'json'
      })
      .pipe(catchError(this.errorHandler.handleError));
  }


  //To check the valid store id , user id and login id for user store mapping import
  public checkValidStoreIdLoginId(rows: Array<{ index: number, storeId?: number, loginId?: string }>, countryName: string): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/userStoreMappingImportExport/checkValidStoreIdLoginId`, { rows, countryName })
      .pipe(catchError(this.errorHandler.handleError));
  }


  //To check already mapped or not
  public checkAlreadyMappedData(rows: Array<{ index: number, userId?: number, storeId?: number, loginId?: string }>): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/userStoreMappingImportExport/checkAlreadyMappedData`, rows)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importUserStoreMapping(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/userStoreMappingImportExport/importUserStoreMapping`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //to export upload price mapping data
  public exportUploadPriceMapping(params?: {
    countryId?: number[],
    searchFilter?: string,
    accountIds?: number[];
  }): Observable<Blob> {

    let queryParams = new HttpParams()
    if (params?.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params?.countryId && params.countryId.length > 0) {
      queryParams = queryParams.set('countryId', params.countryId.join(','));
    }
    if (params?.accountIds && params?.accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }

    const columns: string[] = [
      'Product ID',
      'Account Name',
      'Model Name',
      'RRP',
      'Go Price',
      'Start Date',
      'End Date'
    ]; //export needed columns
    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/PriceMappingImportExport/downloadExcelReport`,
        { columns },
        {
          params: queryParams,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  //To check the account mapped core range for price mapping import
  public accountMappedCoreRangeCheck(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_API}/PriceMappingImportExport/accountMappedCoreRangeCheck`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportPmiIncentiveSummary(params: {
    countryIds?: number[];
    month?: number;
    year?: number;
    searchFilter?: string;
    channelIds?: number[];
    accountIds?: number[];
    incentiveSchemeIds?: number[];
    tierIds?: number[];
    periodType?: string;
  }): Observable<Blob> {
    let queryParams = new HttpParams();
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month != null) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.searchFilter?.trim()) {
      queryParams = queryParams.set('searchFilter', params.searchFilter.trim());
    }
    if (params.channelIds?.length) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds?.length) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.incentiveSchemeIds?.length) {
      queryParams = queryParams.set('incentiveSchemeIds', params.incentiveSchemeIds.join(','));
    }
    if (params.tierIds?.length) {
      queryParams = queryParams.set('tierIds', params.tierIds.join(','));
    }
    if (params.periodType) {
      queryParams = queryParams.set('periodType', params.periodType);
    }
    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/incentiveImportExport/pmiIncentiveSummary/downloadExcelReport`,
        {},
        { params: queryParams, responseType: 'blob' as 'json' }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportPmiIncentiveDetails(params: {
    countryIds?: number[];
    month?: number;
    year?: number;
    searchFilter?: string;
    promoterIds?: number[];
    channelIds?: number[];
    accountIds?: number[];
    incentiveSchemeIds?: number[];
    tierIds?: number[];
    periodType?: string;
  }): Observable<Blob> {
    let queryParams = new HttpParams();
    if (params.countryIds?.length) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.month != null) {
      queryParams = queryParams.set('month', params.month.toString());
    }
    if (params.year != null) {
      queryParams = queryParams.set('year', params.year.toString());
    }
    if (params.searchFilter?.trim()) {
      queryParams = queryParams.set('searchFilter', params.searchFilter.trim());
    }
    if (params.promoterIds?.length) {
      queryParams = queryParams.set('promoterIds', params.promoterIds.join(','));
    }
    if (params.channelIds?.length) {
      queryParams = queryParams.set('channelIds', params.channelIds.join(','));
    }
    if (params.accountIds?.length) {
      queryParams = queryParams.set('accountIds', params.accountIds.join(','));
    }
    if (params.incentiveSchemeIds?.length) {
      queryParams = queryParams.set('incentiveSchemeIds', params.incentiveSchemeIds.join(','));
    }
    if (params.tierIds?.length) {
      queryParams = queryParams.set('tierIds', params.tierIds.join(','));
    }
    if (params.periodType) {
      queryParams = queryParams.set('periodType', params.periodType);
    }
    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/incentiveImportExport/pmiIncentiveDetails/downloadExcelReport`,
        {},
        { params: queryParams, responseType: 'blob' as 'json' }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public exportStoreTargetTemplate(params: {
    countryId: number;
    month: number;
    year: number;
  }): Observable<Blob> {
    let queryParams = new HttpParams()
      .set('countryId', params.countryId.toString())
      .set('month', params.month.toString())
      .set('year', params.year.toString());
    return this.http
      .post<Blob>(
        `${this.RAWDATA_API}/incentiveImportExport/storeTarget/downloadExcelReport`,
        {},
        { params: queryParams, responseType: 'blob' as 'json' }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

}
